import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, CheckCircle2, Circle, AlertTriangle, Loader2 } from "lucide-react";
import { useMatters } from "@/hooks/use-supabase-queries";
import { format, isPast, isToday } from "date-fns";

interface UpcomingMilestonesCardProps {
  onOpenMatter?: (matterId: string) => void;
}

export function UpcomingMilestonesCard({ onOpenMatter }: UpcomingMilestonesCardProps) {
  const { data: matters, isLoading } = useMatters();

  // Derive milestones from matters with estimated completion dates
  const milestones = (matters || [])
    .filter(m => m.estimated_completion && m.status === 'active')
    .map(m => {
      const dueDate = new Date(m.estimated_completion!);
      const status = isPast(dueDate) ? 'overdue' : isToday(dueDate) ? 'due-today' : 'upcoming';
      return {
        id: m.id,
        title: m.title,
        matter: `${m.matter_number} - ${m.title}`,
        dueDate: format(dueDate, 'MMM d, yyyy'),
        status: status as 'overdue' | 'due-today' | 'upcoming',
      };
    })
    .slice(0, 4);

  const statusConfig = {
    upcoming: { icon: Circle, iconClass: "text-muted-foreground", badgeClass: "bg-secondary text-secondary-foreground", label: "Upcoming" },
    "due-today": { icon: CheckCircle2, iconClass: "text-accent", badgeClass: "bg-accent text-accent-foreground", label: "Due Today" },
    overdue: { icon: AlertTriangle, iconClass: "text-destructive", badgeClass: "bg-destructive text-destructive-foreground", label: "Overdue" },
  };

  if (isLoading) {
    return <Card><CardContent className="p-8 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></CardContent></Card>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Calendar className="h-5 w-5 text-primary" />
          Upcoming Milestones
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {milestones.length === 0 ? (
          <p className="text-muted-foreground text-center py-4">No upcoming milestones</p>
        ) : (
          milestones.map((milestone) => {
            const config = statusConfig[milestone.status];
            const Icon = config.icon;
            return (
              <button
                key={milestone.id}
                type="button"
                onClick={() => onOpenMatter?.(milestone.id)}
                className="flex w-full items-start gap-3 rounded-lg p-3 text-left transition-colors hover:bg-muted/50"
              >
                <Icon className={`h-5 w-5 mt-0.5 ${config.iconClass}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-medium text-foreground text-sm">{milestone.title}</h4>
                    <Badge className={config.badgeClass} variant="secondary">{config.label}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground truncate mt-1">{milestone.matter}</p>
                  <p className="text-xs text-primary mt-1">{milestone.dueDate}</p>
                </div>
              </button>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
